import {
  ArrowBack,
  CheckCircleOutlineRounded,
  EditRounded,
  InfoOutlined,
  VisibilityRounded,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import locales from "../../../constants/locales";
import MainLayout from "../../../layouts/main/main_layout";
import Loading from "../../../components/loading/loading";
import { useFeedback } from "../../../providers/feedback/feeedback";
import { queryClient } from "../../../providers/query/query";
import useUser from "../../../providers/user/user_provider";
import {
  approveSchemeTranslation,
  getScheme,
  updateSchemeDetails,
  updateSchemeEligibility,
} from "../../../services/schemes/schemes";
import { SchemeDetailsResponse } from "../models/scheme_details";
import {
  SchemeEligibilityInputs,
  SchemeOtherDetailsInputs,
} from "../models/scheme_form";
import { SchemeProvider } from "../providers/scheme_provider";
import SchemeDetails from "./components/scheme_details";
import SchemeEligilibility from "./components/scheme_eligilibility";
import ApproveTranslationDialog from "./components/approve_translation_dialog";
import {
  black,
  green,
  greenLight,
  greyEnabled,
  orange,
  orangeLightActive,
  purple,
  purpleLight,
} from "../../../theme/colors";

export default function EditScheme({
  readOnly = false,
}: {
  readOnly: boolean;
}) {
  const [detailsSubmitting, setDetailsSubmitting] = useState(false);
  const [eligibilitySubmitting, setEligibilitySubmitting] = useState(false);
  const [eligibilityEdit, setEligibilityEdit] = useState(false);
  const [approving, setApproving] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const { showSnackbar } = useFeedback();
  const { userPermissions } = useUser();
  const [selectedLocale, setSelectedLocale] = useState("en");
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    data,
    isError,
    error: schemeError,
    isLoading: isSchemeLoading,
  } = useQuery<SchemeDetailsResponse>(
    ["scheme-details", id!, selectedLocale],
    async () => {
      let data = await getScheme(id!, selectedLocale);
      return data.data.data;
    },
    {
      retry: 2,
    }
  );

  async function saveSchemeDetails(data: SchemeOtherDetailsInputs) {
    setDetailsSubmitting(true);
    updateSchemeDetails(id!, selectedLocale, data)
      .then((res) => {
        showSnackbar("Scheme details saved successfully");
        queryClient.invalidateQueries({
          queryKey: ["scheme-details", id!, selectedLocale],
        });
        queryClient.invalidateQueries({ queryKey: ["schemes"] });
      })
      .catch((e) => {
        if (e.response?.status === 403) {
          showSnackbar("You do not have permission to perform this operation.");
        } else {
          showSnackbar(
            e?.response?.data?.error?.message ||
              "Something went wrong while saving the scheme details."
          );
        }
      })
      .finally(() => setDetailsSubmitting(false));
  }

  async function updateEligibility(eligibility: SchemeEligibilityInputs) {
    setEligibilitySubmitting(true);
    updateSchemeEligibility(id!, eligibility)
      .then((res) => {
        showSnackbar("Scheme eligibility updated successfully");
        setEligibilityEdit(false);
        queryClient.invalidateQueries({
          queryKey: ["scheme-details", id!, selectedLocale],
        });
        queryClient.invalidateQueries({ queryKey: ["schemes"] });
      })
      .catch((e) => {
        if (e.response?.status === 403) {
          showSnackbar("You do not have permission to perform this operation.");
        } else {
          showSnackbar(
            e?.response?.data?.error?.message ||
              "Something went wrong while updating the eligibility criteria."
          );
        }
      })
      .finally(() => setEligibilitySubmitting(false));
  }

  async function handleApprove() {
    setApproving(true);
    approveSchemeTranslation(id!, selectedLocale)
      .then(() => {
        showSnackbar("Translation approved");
        setApproveDialogOpen(false);
        queryClient.invalidateQueries({
          queryKey: ["scheme-details", id!, selectedLocale],
        });
        queryClient.invalidateQueries({ queryKey: ["schemes"] });
      })
      .catch((e) => {
        if (e.response?.status === 403) {
          showSnackbar("You do not have permission to perform this operation.");
        } else {
          showSnackbar(
            e?.response?.data?.error?.message ||
              "Something went wrong while approving the translation."
          );
        }
      })
      .finally(() => setApproving(false));
  }

  return (
    <MainLayout>
      <SchemeProvider>
        <Grid container spacing={3}>
          <Grid item md={12} sm={12} xs={12} lg={12} xl={10}>
            {/* {hasPermission("Schemes", "Read") ? ( */}
            <Card
              elevation={0}
              sx={{ width: "100%", borderRadius: 3, border: "1px solid #ECECEC", m: 1 }}
            >
              <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                <Box display="flex" flexDirection="row" alignItems="center">
                  <IconButton onClick={() => navigate(-1)}>
                    <ArrowBack />
                  </IconButton>
                  <Box display="flex" alignItems="center" gap={1.5} ml={0.5}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        bgcolor: purpleLight,
                        flexShrink: 0,
                      }}
                    >
                      {readOnly ? (
                        <VisibilityRounded sx={{ color: purple, fontSize: 22 }} />
                      ) : (
                        <EditRounded sx={{ color: purple, fontSize: 22 }} />
                      )}
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight={700} color={black}>
                        {readOnly ? "View scheme" : "Edit scheme"}
                      </Typography>
                      <Typography variant="body2" color={greyEnabled}>
                        {readOnly
                          ? "View the scheme's details and eligibility criteria."
                          : "Update the scheme's details and eligibility criteria."}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                {!isSchemeLoading && data?.orgName && (
                  <Box mt={2} maxWidth={360}>
                    <TextField
                      value={data.orgName}
                      fullWidth
                      label="Organisation"
                      disabled
                    />
                  </Box>
                )}
                <Tabs
                  value={locales.findIndex(
                    (locale) => locale.code === selectedLocale
                  )}
                  onChange={(e, index: number) =>
                    setSelectedLocale(locales[index].code)
                  }
                  TabIndicatorProps={{
                    style: { backgroundColor: purple, height: 3, borderRadius: 3 },
                  }}
                  sx={{ mt: 2 }}
                >
                  {locales.map((locale, index) => (
                    <Tab
                      label={locale.name}
                      sx={{
                        textTransform: "capitalize",
                        maxWidth: 50,
                        color: greyEnabled,
                        "&.Mui-selected": { color: purple },
                      }}
                    />
                  ))}
                </Tabs>
                <Divider />

                {!isSchemeLoading && data?.isTranslated === false && (
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    sx={{
                      mt: 2,
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: orangeLightActive,
                      border: `1px solid ${orangeLightActive}`,
                    }}
                  >
                    <InfoOutlined sx={{ color: orange, fontSize: 20 }} />
                    <Typography variant="body2" color={black}>
                      This scheme has no content in{" "}
                      {locales.find((locale) => locale.code === selectedLocale)
                        ?.name || selectedLocale}{" "}
                      yet — fill in the details below to add this translation.
                    </Typography>
                  </Box>
                )}

                {!isSchemeLoading &&
                  data?.isTranslated === true &&
                  data?.isApproved === false && (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={1}
                      flexWrap="wrap"
                      sx={{
                        mt: 2,
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: orangeLightActive,
                        border: `1px solid ${orangeLightActive}`,
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <InfoOutlined sx={{ color: orange, fontSize: 20 }} />
                        <Typography variant="body2" color={black}>
                          The{" "}
                          {locales.find(
                            (locale) => locale.code === selectedLocale
                          )?.name || selectedLocale}{" "}
                          translation is pending approval.
                        </Typography>
                      </Box>
                      {!readOnly && userPermissions?.superAdmin && (
                        <Button
                          size="small"
                          variant="contained"
                          disableElevation
                          disabled={approving}
                          onClick={() => setApproveDialogOpen(true)}
                          startIcon={
                            approving ? (
                              <CircularProgress
                                size={16}
                                sx={{ color: "#fff" }}
                              />
                            ) : (
                              <CheckCircleOutlineRounded />
                            )
                          }
                          sx={{
                            bgcolor: purple,
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 600,
                            "&:hover": { bgcolor: "#7A2074" },
                          }}
                        >
                          Approve
                        </Button>
                      )}
                    </Box>
                  )}

                {!isSchemeLoading &&
                  data?.isTranslated === true &&
                  data?.isApproved === true &&
                  data?.approvedByName && (
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      sx={{
                        mt: 2,
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: greenLight,
                        border: `1px solid ${greenLight}`,
                      }}
                    >
                      <CheckCircleOutlineRounded
                        sx={{ color: green, fontSize: 20 }}
                      />
                      <Typography variant="body2" color={black}>
                        Approved by {data.approvedByName}
                      </Typography>
                    </Box>
                  )}

                {isSchemeLoading ? (
                  <Box
                    height={300}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Loading />
                  </Box>
                ) : (
                  <SchemeDetails
                    isError={isError}
                    errorMessage={
                      (schemeError as any)?.response?.data?.error?.message ||
                      "Something went wrong while loading the scheme details."
                    }
                    readOnly={readOnly}
                    openEligibility={() => {
                      setEligibilityEdit(true);
                    }}
                    data={data}
                    onSave={saveSchemeDetails}
                    submitting={detailsSubmitting}
                  />
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        {eligibilityEdit && (
          <SchemeEligilibility
            isOpen={eligibilityEdit}
            onClose={() => {
              setEligibilityEdit(false);
            }}
            data={data}
            onSave={(data: SchemeEligibilityInputs) => {
              updateEligibility(data);
            }}
            submitting={eligibilitySubmitting}
          />
        )}
        {approveDialogOpen && (
          <ApproveTranslationDialog
            open={approveDialogOpen}
            languageName={
              locales.find((locale) => locale.code === selectedLocale)?.name
            }
            onClose={() => setApproveDialogOpen(false)}
            onConfirm={handleApprove}
            submitting={approving}
          />
        )}
      </SchemeProvider>
    </MainLayout>
  );
}
